<template>
  <div :class="['flatlay-layout', layoutUtilities.layoutClass]">
    <div
      class="tile-row-1"
      :style="{
        '--first-row-height': `${firstRowHeight}px`,
        '--row-height': `${firstRowHeightAdjusted}px`
      }">
      <FlatlayTile
        v-if="tilePresence.a"
        ref="tileARef"
        :size="tileSizes[0]"
        :class="['tile-a', layoutUtilities.tileClasses[0]]"
        :image="tileImages.a">
        <template #default/>
      </FlatlayTile>
      <FlatlayTile
        v-if="tilePresence.b"
        ref="tileBRef"
        :size="tileSizes[1]"
        :class="['tile-b', layoutUtilities.tileClasses[1]]"
        :image="tileImages.b">
        <template #default/>
      </FlatlayTile>
    </div>
    <div
      class="tile-row-2"
      :style="{ gridTemplateRows: `${smallestTileHeight}px` }">
      <FlatlayTile
        v-if="tilePresence.c"
        ref="tileCRef"
        :size="tileSizes[2]"
        :class="['tile-c', layoutUtilities.tileClasses[2]]"
        :image="tileImages.c">
        <template #default/>
      </FlatlayTile>
      <FlatlayTile
        v-if="tilePresence.f"
        ref="tileFRef"
        :size="tileSizes[3]"
        :class="['tile-f', layoutUtilities.tileClasses[3]]"
        :image="tileImages.accessoryImageUrl">
        <template #default/>
      </FlatlayTile>
    </div>
  </div>
</template>

<script setup>
import { computed, useTemplateRef } from 'vue'
import { flatlays, getFlatlayTileSizes, getFlatlayUtilityClasses } from '@/utils/flatlays'
import { useElementPosition } from '@/composables/element'
import FlatlayTile from '@/components/outfitBuilder/flatlays/FlatlayTile.vue'

const props = defineProps({
  layout: {
    type: String,
    required: true,
    validator: (value) => flatlays.includes(value)
  },
  tileImages: {
    type: Object,
    required: true
  }
})
const tileSizes = computed(() => getFlatlayTileSizes(props.layout))
const layoutUtilities = computed(() => getFlatlayUtilityClasses(props.layout))
const tilePresence = computed(() => layoutUtilities.value.tilePresence)

const tileARef = useTemplateRef('tileARef')
const tileBRef = useTemplateRef('tileBRef')
const tileCRef = useTemplateRef('tileCRef')
const tileFRef = useTemplateRef('tileFRef')

const tileAElement = computed(() => tileARef.value?.$el ?? null)
const tileBElement = computed(() => tileBRef.value?.$el ?? null)
const tileCElement = computed(() => tileCRef.value?.$el ?? null)
const tileFElement = computed(() => tileFRef.value?.$el ?? null)

const tileAPosition = useElementPosition(tileAElement)
const tileBPosition = useElementPosition(tileBElement)
const tileCPosition = useElementPosition(tileCElement)
const tileFPosition = useElementPosition(tileFElement)

const tileHeights = computed(() => ([
  tileAPosition.value.height,
  tileBPosition.value.height,
  tileCPosition.value.height,
  tileFPosition.value.height
].filter(height => height > 0)))

const smallestTileHeight = computed(() => {
  if (!tileHeights.value.length) return 0
  return Math.min(...tileHeights.value)
})

const largestTileHeight = computed(() => {
  if (!tileHeights.value.length) return 0
  return Math.max(...tileHeights.value)
})

const tileHeightForF = computed(() => tileFPosition.value.height || 0)
const hasLargeTile = computed(() => tileSizes.value.includes('l'))
const firstRowHeight = computed(() => {
// if there is a large tile, do large tile minus f height. if there is no large tile, do smallest tile height.
  if (hasLargeTile.value) {
    return Math.max(0, largestTileHeight.value - tileHeightForF.value)
  }
  return smallestTileHeight.value
})
const firstRowHeightAdjusted = computed(() => {
  if (hasLargeTile.value) return firstRowHeight.value
  return Math.max(0, firstRowHeight.value * 0.95)
})
</script>

<style lang="scss" scoped>
.flatlay-layout {
  width: 100%;
  aspect-ratio: 3/4;
}

.tile-row-1 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  height: var(--row-height);
  align-items: start;
}

.tile-row-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: end;
}

$flatlay-default-tiles: (
  a: (x: 0px, y: 0px, z: 4),
  b: (x: -5%, y: 0px, z: 3),
  c: (x: 0px, y: 0%, z: 2),
  f: (x: -5%, y: 0%, z: 4)
);

$flatlay-f-right-column: (
  f: (x: -5%, y: 0, z: 2, grid-column: 2)
);

$flatlay-layouts: (
  'AS-BS-CS-F': $flatlay-default-tiles,
  'AS-BS-CM-F': $flatlay-default-tiles,
  'AS-BM-CS-F': $flatlay-default-tiles,
  'AS-BM-CM-F': $flatlay-default-tiles,
  'AL-BS-F': map-merge($flatlay-default-tiles, $flatlay-f-right-column),
  'AL-BM-F': map-merge($flatlay-default-tiles, $flatlay-f-right-column),
  'AL-BL-F': map-merge($flatlay-default-tiles, $flatlay-f-right-column),
  'AS-CS-F': map-merge($flatlay-default-tiles, $flatlay-f-right-column),
  'AS-CM-F': map-merge($flatlay-default-tiles, $flatlay-f-right-column)
);

$flatlay-tiles: ('a', 'b', 'c', 'f');


@each $layout, $config in $flatlay-layouts {
  .layout-#{$layout} {

    @each $tile in $flatlay-tiles {
      $tile-config: map-get($config, $tile);

      .tile-#{$tile} {
        transform: translate(map-get($tile-config, x), map-get($tile-config, y));
        z-index: map-get($tile-config, z);
        grid-column: map-get($tile-config, grid-column);
      }
    }
  }
}
</style>